import { Button, Heading, Alert } from '@mm/components';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';

import styles from './EducationalCardsBlock.module.css';
import classNames from 'classnames';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { XRayGrid } from '../../Integrations/NativeIntegrations/components/XRayIngegrations/XRayIntegrations';
import { XRayIcon } from '@mm/icons';
import xrayTable from 'assets/xray-table.png';
import {
  useFreeTrialUpgradeModal,
  useShowTrialEndAlert,
} from '../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { landmineNames } from '@mm/constants/landmine-names';
import { DashboardXRayAnalytics } from '../../Analytics/XRayAnalytics/AnalyticsCharts/DashboardXRayAnalytics';
import { PlusOutlined } from '@ant-design/icons';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import { ActivationBlock } from '../ActivationBlock/ActivationBlock';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export const EducationalCardsBlock = ({
  isMobile,
  isShopify,
}: {
  isMobile?: boolean;
  isShopify?: boolean;
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const activeFacebookPage = useSelector(selectActiveFacebookPage);

  const firstChatbotId = useSelector(
    (state: StoreState) => state.chatbot.basicListByCompany[0]?.id,
  );
  const botId = chatbotId || firstChatbotId;
  const history = useHistory();
  const { retrieveAll, create } = usePromoterCRUDActions();
  const { showSlideoutMenu } = useViewActions();

  const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();

  const activatedXRay =
    activeFacebookPage?.getemailsActivated ||
    activeFacebookPage?.onceSuccessfullyLoadedGlobalJs;

  const getEmailPromoters = useSelector((state: StoreState) =>
    (state.promoter.entities || [])
      .filter(p => p?.botId?.toString() === botId?.toString())
      .filter(p => p.type === 'getemails'),
  );

  const xrayGridKeys = isMobile
    ? ['name', 'active', 'leads_captured']
    : [
        'name',
        'active',
        'leads_captured',
        'engagement_ratio',
        'from',
        'actions',
      ];

  const redirect = useCallback(
    (path: string) => {
      if (botId) {
        history.push(path);
      }
    },
    [botId, history],
  );

  const onAddPromoterClick = async () => {
    const promoter = await create({
      botId,
      type: 'getemails',
      active: false,
    } as PromoterModel);

    if (promoter.id) {
      showSlideoutMenu('getemailsEditorNew', {
        promoterId: promoter.id,
      });
    }
  };

  const freeTrialUpgradeModal = useFreeTrialUpgradeModal();

  const showTrialEndAlert = useShowTrialEndAlert(freeTrialUpgradeModal);

  useEffect(() => {
    // pagination: {page: 3, perPage: 3 }
    retrieveAll(botId, 'getemails');
  }, []);

  return (
    <div className={styles.container}>
      <Heading size={2} className={styles.heading}>
        Capture, qualify, and close leads automatically in 15 minutes.
      </Heading>
      {isMobile && !isShopify && (
        <ActivationBlock className={styles.activationBlockMobile} />
      )}
      {getEmailPromoters.length && activatedXRay ? (
        <>
          <div className={classNames(styles.card, styles.xray)}>
            <DashboardXRayAnalytics />
          </div>
          <div className={classNames(styles.card, styles.xray)}>
            {showTrialEndAlert && (
              <Alert
                css={{
                  container: styles.trialEndAlert,
                }}
                borderRadius={0}
                showCloseIcon={false}
                type="warning"
                showHeader
              >
                <div>
                  <div>
                    <span className={styles.trialEndAlertTitle}>
                      Leads are not being captured
                    </span>
                    <span className={styles.trialEndAlertSubtitle}>
                      Please upgrade your plan to unlock all features and
                      continue capturing leads
                    </span>
                  </div>

                  <Button
                    onClick={e => {
                      e?.preventDefault();
                      freeTrialUpgradeModal.openFreeTrialUpgradeModal(
                        {
                          landmineName: landmineNames.XRAY_DASHBOARD,
                        },
                        e,
                      );
                    }}
                  >
                    Upgrade Plan
                  </Button>
                </div>
              </Alert>
            )}
            <div className={styles.cardHeadingWithButton}>
              <Heading size={3} className={styles.cardHeading}>
                {pixelName} Triggers
                <span className={styles.cardSubtitle}>
                  allow us to gather leads from your website traffic
                </span>
              </Heading>
              <div className={styles.buttonsWrapper}>
                <Button
                  color="secondary-blue"
                  onClick={() =>
                    redirect(
                      BotBuilderRoutes.xRay.getLink({
                        chatbotId: botId,
                      }),
                    )
                  }
                  css={{ container: styles.secondaryButton }}
                >
                  Go to All Triggers ({getEmailPromoters.length})
                </Button>

                <Button
                  leftIcon={<PlusOutlined />}
                  onClick={onAddPromoterClick}
                  css={{ container: styles.actionButton }}
                >
                  Trigger
                </Button>
              </div>
            </div>

            <XRayGrid
              botId={botId}
              className={styles.table}
              cap={3}
              columnsKeys={xrayGridKeys}
              gridTemplateColumns={
                isMobile ? '50% 25% 25%' : '20% 20% 15% 15% 20% 10%'
              }
              limitedTableKeys
            />
          </div>
        </>
      ) : activatedXRay ? null : (
        <div className={classNames(styles.card, styles.xray)}>
          <div className={styles.xrayCardHeaderRow}>
            <XRayIcon color="#AD6CD6" className={styles.xrayIcon} />
            <div>
              <div className={styles.xrayStepHeader}> Step 1.</div>
              <Heading size={3} className={styles.cardHeading}>
                Install {pixelName}
              </Heading>
            </div>
          </div>
          <div className={styles.xrayInfoRow}>
            <div className={styles.xrayTableImage}>
              <img src={xrayTable} alt="{pixelName} Results Table" />
            </div>
            <div className={styles.xrayTextColumn}>
              <div className={styles.xrayTextHeading}>
                {pixelName} captures leads from your anonymous web traffic
              </div>
              <div>
                Install the script & capture leads without coupons, popups, or
                widgets.
              </div>
              <div>Automatic anonymous lead capture.</div>
            </div>
          </div>
          <div className={styles.cardDescriptionBlock}>
            <div className={styles.buttonsWrapper}>
              <PrimaryButton
                className={classNames(
                  styles.actionButton,
                  styles.actionButtonXray,
                )}
                onClick={() =>
                  redirect(
                    BotBuilderRoutes.xRay.getLink({
                      chatbotId: botId,
                    }),
                  )
                }
              >
                Install {pixelName}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
